import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AffiliateCard from "@/components/affiliates/AffiliateCard";
import { COMMUNITY_PROFILES } from "@/data/communityProfiles";
import {
  MapPin,
  CalendarDays,
  ArrowRight,
  Users,
  Sparkles,
  HandHeart,
} from "lucide-react";

// House org: used mainly to exercise site features — kept out of the homepage
// directory listings so partner organizations get the visibility.
const HOUSE_ORG_SLUG = "the-emerging-market";

const formatEventDate = (iso?: string | null) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return {
      month: d.toLocaleDateString("en-US", { month: "short" }),
      day: d.toLocaleDateString("en-US", { day: "2-digit" }),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    };
  } catch {
    return null;
  }
};

const formatEventTime = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return "";
  }
};

type PartnerEvent = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  url: string | null;
  starts_at: string;
  ends_at: string | null;
  focus_area: string | null;
  kind: string | null;
  affiliates: { name: string; slug: string } | null;
};

type CaseStudyRow = {
  project_id: string;
  idea_id: string;
  idea_title: string;
  idea_description: string | null;
  idea_category: string | null;
  idea_image_url: string | null;
  author_display_name: string | null;
  latest_outcome: string;
  latest_summary: string;
  latest_lesson_at: string;
  public_lessons_count: number;
  tags: string[] | null;
};

type AffiliateRow = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  location: string | null;
  category: string;
  services: string[];
  logo_url: string | null;
  featured: boolean;
  tier: string;
};

type ArticleRow = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  author: string | null;
};

const SectionHeader = ({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: { label: string; href: string };
}) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
    <div className="max-w-2xl">
      <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">{eyebrow}</span>
      <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 text-foreground">{title}</h2>
      {description && <p className="text-muted-foreground mt-2 max-w-xl">{description}</p>}
    </div>
    {action && (
      <Link
        to={action.href}
        className="inline-flex items-center gap-1 text-sm text-primary font-semibold hover:underline shrink-0"
      >
        {action.label} <ArrowRight className="h-4 w-4" />
      </Link>
    )}
  </div>
);

const EmptyState = ({
  icon: Icon,
  title,
  body,
  cta,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  cta?: { label: string; href: string };
}) => (
  <Card className="p-8 md:p-10 bg-cream border-dashed border-border/60 text-center">
    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <h3 className="font-display font-semibold text-lg mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">{body}</p>
    {cta && (
      <Button asChild variant="outline" size="sm">
        <Link to={cta.href}>{cta.label}</Link>
      </Button>
    )}
  </Card>
);

const CommunitySections = () => {
  const { data: partnerEvents = [], isLoading: eventsLoading } = useQuery({
    queryKey: ["communities-partner-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_events")
        .select(
          "id,title,description,location,url,starts_at,ends_at,focus_area,kind,partner_org_id,affiliates:partner_org_id(name,slug)",
        )
        .eq("published", true)
        .gte("starts_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order("starts_at", { ascending: true })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as PartnerEvent[];
    },
  });

  const { data: caseStudies = [], isLoading: casesLoading } = useQuery({
    queryKey: ["communities-case-studies"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_case_studies");
      if (error) throw error;
      return (data ?? []) as CaseStudyRow[];
    },
  });

  const { data: leaders = [], isLoading: leadersLoading } = useQuery({
    queryKey: ["communities-advocates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id,slug,name,tagline,location,category,services,logo_url,featured,tier")
        .eq("status", "active")
        .eq("is_published", true)
        .eq("tier", "advocate")
        .neq("slug", HOUSE_ORG_SLUG)
        .order("featured", { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as AffiliateRow[];
    },
  });

  const { data: stories = [], isLoading: storiesLoading } = useQuery({
    queryKey: ["communities-stories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("id,slug,title,excerpt,category,author")
        .eq("published", true)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(6);
      if (error) throw error;
      return (data ?? []) as ArticleRow[];
    },
  });

  const { data: partners = [], isLoading: partnersLoading } = useQuery({
    queryKey: ["communities-partners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id,slug,name,tagline,location,category,services,logo_url,featured,tier")
        .eq("status", "active")
        .eq("is_published", true)
        .neq("slug", HOUSE_ORG_SLUG)
        .order("featured", { ascending: false })
        .limit(6);
      if (error) throw error;
      return (data ?? []) as AffiliateRow[];
    },
  });

  return (
    <>
        {/* Events & workshops */}
        <section className="py-16 md:py-24 border-b border-border/40">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeader
              eyebrow="Events & workshops"
              title="Show up for the work"
              description="Trainings, roundtables, and partner events designed to help Alabama organizations build capacity and make connections."
              action={{ label: "View all events", href: "/events" }}
            />

            {eventsLoading ? (
              <div className="grid md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="h-40 bg-card/50 animate-pulse" />
                ))}
              </div>
            ) : partnerEvents.length === 0 ? (
              <EmptyState
                icon={CalendarDays}
                title="No upcoming events"
                body="Check back soon for workshops, trainings, and partner roundtables across Alabama."
                cta={{ label: "Browse the events page", href: "/events" }}
              />
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {partnerEvents.map((event) => {
                  const date = formatEventDate(event.starts_at);
                  return (
                    <Card key={event.id} className="p-5 bg-background border-border hover:border-primary/30 transition-colors">
                      <div className="flex items-start gap-4">
                        {date && (
                          <div className="shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center text-primary">
                            <span className="text-[10px] uppercase tracking-wider font-semibold">
                              {date.month}
                            </span>
                            <span className="font-display text-xl font-bold leading-none">{date.day}</span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-semibold mb-1 line-clamp-2">{event.title}</h3>
                          {event.affiliates?.name && (
                            <p className="text-xs text-primary font-medium mb-2">{event.affiliates.name}</p>
                          )}
                          {event.location && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                              <MapPin className="h-3 w-3" /> {event.location}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {date?.weekday} · {formatEventTime(event.starts_at)}
                          </p>
                        </div>
                      </div>
                      {event.description && (
                        <p className="text-sm text-muted-foreground mt-4 line-clamp-2">{event.description}</p>
                      )}
                      {event.url ? (
                        <a
                          href={event.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary font-semibold mt-4 hover:underline"
                        >
                          Details <ArrowRight className="h-3 w-3" />
                        </a>
                      ) : null}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Featured wins / case studies */}
        <section className="py-16 md:py-24 border-b border-border/40 bg-background/50">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeader
              eyebrow="Featured wins"
              title="Community-led lessons"
              description="Real projects from Alabama organizations, with honest notes on what worked, what didn’t, and what’s next."
              action={{ label: "Read all case studies", href: "/archive/case-studies" }}

            />

            {casesLoading || storiesLoading ? (
              <div className="grid md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="h-48 bg-card/50 animate-pulse" />
                ))}
              </div>
            ) : caseStudies.length === 0 && stories.length === 0 ? (
              <EmptyState
                icon={Sparkles}
                title="Case studies coming soon"
                body="We’re collecting lessons from community projects across Alabama. Have a story to share?"
                cta={{ label: "Share an idea or project", href: "/ideas" }}
              />
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {caseStudies.slice(0, 3).map((study) => (
                  <Link key={study.project_id} to={`/archive/case-studies/${study.project_id}`}>

                    <Card className="h-full p-5 bg-background border-border hover:border-primary/30 transition-colors group">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" className="text-[10px] uppercase">
                          {study.idea_category ?? "Community project"}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {study.public_lessons_count} lesson{study.public_lessons_count === 1 ? "" : "s"}
                        </span>
                      </div>
                      <h3 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                        {study.idea_title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{study.latest_summary}</p>
                      {study.author_display_name && (
                        <p className="text-xs text-muted-foreground">Shared by {study.author_display_name}</p>
                      )}
                    </Card>
                  </Link>
                ))}
                {stories.slice(0, Math.max(0, 3 - caseStudies.length)).map((story) => (
                  <Link key={story.id} to={`/insights/${story.slug}`}>
                    <Card className="h-full p-5 bg-background border-border hover:border-primary/30 transition-colors group">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" className="text-[10px] uppercase">
                          {story.category ?? "Analysis"}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">Insight</span>
                      </div>
                      <h3 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                        {story.title}
                      </h3>
                      {story.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{story.excerpt}</p>
                      )}
                      {story.author && (
                        <p className="text-xs text-muted-foreground">By {story.author}</p>
                      )}
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Community leaders */}
        <section className="py-16 md:py-24 border-b border-border/40">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeader
              eyebrow="Community leaders"
              title="Advocates on the ground"
              description="Alabama individuals and small organizations working directly in communities and sharing what they learn."
              action={{ label: "See all advocates", href: "/affiliates" }}
            />

            {leadersLoading ? (
              <div className="grid md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="h-40 bg-card/50 animate-pulse" />
                ))}
              </div>
            ) : leaders.length === 0 ? (
              <EmptyState
                icon={HandHeart}
                title="Advocates joining soon"
                body="We’re building a directory of Alabama community advocates. Want to be listed?"
                cta={{ label: "Apply to join", href: "/partners/apply?roles=advocate" }}
              />
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {leaders.map((leader, i) => (
                  <AffiliateCard key={leader.id} affiliate={leader} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Partner directory */}
        <section className="py-16 md:py-24 border-b border-border/40 bg-background/50">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeader
              eyebrow="Partner directory"
              title="Organizations to work with"
              description="Vetted partners across Alabama who can help with advocacy, communications, research, and capital."
              action={{ label: "Browse partners", href: "/partners" }}
            />

            {partnersLoading ? (
              <div className="grid md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="h-40 bg-card/50 animate-pulse" />
                ))}
              </div>
            ) : partners.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Partner directory loading"
                body="We’re onboarding Alabama partners. Check back or apply to join the network."
                cta={{ label: "Apply to partner", href: "/partners/apply?roles=partner" }}
              />
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {partners.map((partner, i) => (
                  <AffiliateCard key={partner.id} affiliate={partner} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Community archive */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeader
              eyebrow="Community archive"
              title="Explore the places we work"
              description="Narrative profiles that tie local history and aspirations to live ideas, projects, legislation, and partners."
              action={{ label: "View full archive", href: "/archive" }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {COMMUNITY_PROFILES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/communities/${c.slug}`}
                  className="group bg-background border border-border rounded-xl p-6 hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {c.kind}
                    </Badge>
                    {c.parent && <span>· {c.parent}</span>}
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-1 group-hover:text-primary transition-colors">
                    {c.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{c.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
    </>
  );
};

export default CommunitySections;
